/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 共有 AppShell の Sidebar に渡すナビゲーショングループを、ページアクセス判定（usePageAccess）と
 *  i18n 翻訳（react-i18next）に基づいて構築するフック。PIL 固有のメニュー定義をここに集約する
 * @graph-connects usePageAccess [calls] canAccessPage(SSOT) でページ表示可否を判定
 */
import { useTranslation } from 'react-i18next';
import { PILAB_NAV_AREA_PATHS, type PilabNavArea } from '@cortex/core-types';
import type { SidebarNavGroup } from '@cortex/web-design-system/components/layout/Sidebar';
import { usePageAccess } from '../../hooks/usePageAccess.js';
import type { PageKey } from '@cortex/domain-product-innovation-lab-types';
import { improvementsNav, orgIssuesNav, teamMeetingsNav, type NavDef } from './nav-defs.js';

/**
 * 親項目のキー → 子としてインデント表示したい項目キーの map。
 * 子が defs に含まれていても、親が defs に含まれない（権限欠落）場合は
 * 「孤児ネスト」になるため、`nested: true` を付与しない。
 * @graph-connects none
 */
const NESTED_CHILD_TO_PARENT: ReadonlyMap<PilabNavArea, PilabNavArea> = new Map([
  ['kpi', 'dashboards'],
  ['reliability', 'dashboards'],
  ['allPjBoard', 'projects'],
  ['pjSandbox', 'projects'],
  ['specRequirements', 'projects'],
  ['basicDesign', 'projects'],
  ['pmoBoard', 'projects'],
  ['assignmentHistory', 'projects'],
  ['lightTasks', 'projects'],
  ['acDivBoard', 'projects'],
  ['mallDivBoard', 'projects'],
  ['logiDivBoard', 'projects'],
  ['engineerBoard', 'piDivManagement'],
  ['idtKpi', 'piDivManagement'],
  ['bugEvaluation', 'piDivManagement'],
  ['qaResponsibility', 'piDivManagement'],
  ['engineerEvaluation', 'piDivManagement'],
  ['qa', 'piDivManagement'],
  ['ccUsage', 'sreManagement'],
  ['ccCommandMatrix', 'sreManagement'],
  ['mcpUsage', 'sreManagement'],
]);

/**
 * ページ遷移を持たず、配下 subpage をまとめる「フォルダ」項目のキー集合。
 * ここに含まれる親項目はクリックしても遷移せず、子の展開/折り畳みのみを行う。
 * @graph-connects none
 */
const FOLDER_KEYS: ReadonlySet<string> = new Set([
  'dashboards',
  'piDivManagement',
  'projects',
  'sreManagement',
]);

/**
 * aC/Mall/Logi Div看板で共通利用する Div（建物）アイコン。事業部向け案件ボードを表す。
 * @graph-connects none
 */
const divBoardIcon = (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

/**
 * ダッシュボード系ページ（KPI / 品質・信頼性）をまとめるフォルダアイコン（プレゼンチャート）。
 * @graph-connects none
 */
const dashboardsNav: NavDef = {
  key: 'dashboards',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 13v-1m4 1v-3m4 3V8M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
      />
    </svg>
  ),
};

/** @graph-connects none */
const kpiNav: NavDef = {
  key: 'kpi',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  ),
};

/** @graph-connects none */
const reliabilityNav: NavDef = {
  key: 'reliability',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  ),
};

/** @graph-connects none */
const qaNav: NavDef = {
  key: 'qa',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  ),
};

/** @graph-connects none */
const engineerBoardNav: NavDef = {
  key: 'engineerBoard',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
      />
    </svg>
  ),
};

/** @graph-connects none */
const idtKpiNav: NavDef = {
  key: 'idtKpi',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
      />
    </svg>
  ),
};

/**
 * PI Div の機能横断フォルダアイコン（組織/ビルディング）。品質・メンバー・評価系ページをまとめる。
 * @graph-connects none
 */
const piDivManagementNav: NavDef = {
  key: 'piDivManagement',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  ),
};

/** @graph-connects none */
const projectsNav: NavDef = {
  key: 'projects',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
      />
    </svg>
  ),
};

/** @graph-connects none */
const allPjBoardNav: NavDef = {
  key: 'allPjBoard',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 10h16M4 14h16M4 18h16"
      />
    </svg>
  ),
};

/** PJ Sandbox（PJ 予定シミュレーション白板）ナビ。ガント×テトリス風の自由編集ボードを表す。 @graph-connects none */
const pjSandboxNav: NavDef = {
  key: 'pjSandbox',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 5a1 1 0 011-1h6v7H4V5zm0 8h6v6H5a1 1 0 01-1-1v-5zm9-9h6a1 1 0 011 1v4h-7V4zm0 7h7v7a1 1 0 01-1 1h-6v-8z"
      />
    </svg>
  ),
};

/** 要件定義の成果物ナビ。案件のナラティブ・業務フロー・レビュー状態を読む入口。 @graph-connects none */
const specRequirementsNav: NavDef = {
  key: 'specRequirements',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
};

/** 基本設計の成果物ナビ。影響一覧・リスク一覧を親チケットごとに読む入口。 @graph-connects none */
const basicDesignNav: NavDef = {
  key: 'basicDesign',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z"
      />
    </svg>
  ),
};

/** アサイン履歴ナビ。全体PJ看板で行ったアサインの記録を時系列で読む入口。 @graph-connects none */
const assignmentHistoryNav: NavDef = {
  key: 'assignmentHistory',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
};

/** @graph-connects none */
const pmoBoardNav: NavDef = {
  key: 'pmoBoard',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
      />
    </svg>
  ),
};

/** @graph-connects none */
const lightTasksNav: NavDef = {
  key: 'lightTasks',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  ),
};

/** @graph-connects none */
const acDivBoardNav: NavDef = {
  key: 'acDivBoard',
  icon: divBoardIcon,
};

/** @graph-connects none */
const mallDivBoardNav: NavDef = {
  key: 'mallDivBoard',
  icon: divBoardIcon,
};

/** @graph-connects none */
const logiDivBoardNav: NavDef = {
  key: 'logiDivBoard',
  icon: divBoardIcon,
};

/** @graph-connects none */
const accountsNav: NavDef = {
  key: 'accounts',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  ),
};

/** @graph-connects none */
const bugEvaluationNav: NavDef = {
  key: 'bugEvaluation',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  ),
};

/** @graph-connects none */
const qaResponsibilityNav: NavDef = {
  key: 'qaResponsibility',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
};

/** @graph-connects none */
const engineerEvaluationNav: NavDef = {
  key: 'engineerEvaluation',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  ),
};

/** @graph-connects none */
const dbDictionaryNav: NavDef = {
  key: 'dbDictionary',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
      />
    </svg>
  ),
};

/** @graph-connects none */
const knowledgeWikiNav: NavDef = {
  key: 'knowledgeWiki',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  ),
};

/** @graph-connects none */
const ccUsageNav: NavDef = {
  key: 'ccUsage',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 12l4-4 4 4 6-6 4 4M5 20h14"
      />
    </svg>
  ),
};

/**
 * AI 規定コマンド利用状況 (人 / PJ × 工程マトリクス) ナビ。グリッド (表) アイコン。
 * @graph-connects none
 */
const ccCommandMatrixNav: NavDef = {
  key: 'ccCommandMatrix',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 10h18M3 14h18M10 3v18M14 3v18M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z"
      />
    </svg>
  ),
};

/** @graph-connects none */
const mcpUsageNav: NavDef = {
  key: 'mcpUsage',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 12h10M4 18h7M17 14l4 4-4 4M21 18h-8"
      />
    </svg>
  ),
};

/**
 * SRE（インフラ・利用量モニタリング）系ページをまとめるフォルダアイコン（サーバー/歯車）。
 * @graph-connects none
 */
const sreManagementNav: NavDef = {
  key: 'sreManagement',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-7-4h.01M12 16h.01"
      />
    </svg>
  ),
};

/** CuttySark 賞（四半期表彰）の予選・本戦。全社イベントなので全社員に開放。 @graph-connects none */
const cuttysarkNav: NavDef = {
  key: 'cuttysark',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 21h8m-4-4v4m-5-17h10v5a5 5 0 01-10 0V4zm10 1h3v2a3 3 0 01-3 3M7 5H4v2a3 3 0 003 3"
      />
    </svg>
  ),
};

/**
 * チームの担当（DLE / DLD / TechLead / チームリーダー）。
 *
 * 参照は内部メンバー全員に開く（誰が要件定義レビューの担当かは案件を進める全員が知りたい）。
 * 設定できるのは CTO / PMO だけで、その制御は画面の中で行う。
 * @graph-connects none
 */
const teamAssignmentsNav: NavDef = {
  key: 'teamAssignments',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  ),
};

/** オペレーション（opetan）セクション入口。全社員に開放。 @graph-connects none */
const operationsNav: NavDef = {
  key: 'operations',
  icon: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.572-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.827-3.31 2.37-2.37.997.608 2.297.07 2.573-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  ),
};

/**
 * 共有 Sidebar 用のナビゲーショングループを構築するフック。
 *
 * ユーザー権限に応じて表示するメニュー項目を絞り込み、ラベルは i18n で翻訳済みの
 * 文字列にして返す。PIL のナビは単一グループ（見出しなし）で構成する。
 *
 * @returns SidebarNavGroup 配列
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 権限・i18n を反映した PIL のサイドバーナビゲーション定義を生成する
 * @graph-connects usePageAccess [calls] canAccessPage(SSOT) でページ表示可否を判定
 */
export function useNavGroups(): SidebarNavGroup[] {
  const { t } = useTranslation();
  const { canAccess } = usePageAccess();

  // 表示順を固定した全ナビ定義。各項目の表示可否は canAccessPage(SSOT) に委譲し、
  // ルートガード(ProtectedRoute)と完全に同一のロジックで判定する。これにより
  // 「メニューに出ないが URL 直打ちで開ける」不整合を防ぐ。
  // PL/主担当 案件一覧は my-page の「看板」タブで提供するため、サイドバーには別エントリを追加しない。
  // githubActivity はメニューから撤去済み（直接URLでは引き続きアクセス可、
  // ProtectedRoute のガードはそのまま維持）。dailyReports は生成側ごと廃止した。項目が多くなり見つけにくくなったため、
  // PI Div 横断機能は piDivManagement、SRE/インフラ系は sreManagement のフォルダにまとめる。
  // membersNav（/members）も同様にメニューから撤去済み。/members/index.tsx が
  // /members/engineer-board への redirect スタブのみで実体を持たないため、フォルダ内で
  // engineerBoard と全く同じ遷移先になる重複リンクだった。members PageKey 自体は
  // /members/$email（メンバー詳細）のガードに使うため canAccessPage 側は変更しない。
  const allDefs: NavDef[] = [
    dashboardsNav,
    kpiNav,
    reliabilityNav,
    piDivManagementNav,
    engineerBoardNav,
    idtKpiNav,
    bugEvaluationNav,
    qaResponsibilityNav,
    engineerEvaluationNav,
    qaNav,
    projectsNav,
    allPjBoardNav,
    pjSandboxNav,
    specRequirementsNav,
    basicDesignNav,
    pmoBoardNav,
    assignmentHistoryNav,
    lightTasksNav,
    acDivBoardNav,
    mallDivBoardNav,
    logiDivBoardNav,
    accountsNav,
    teamAssignmentsNav,
    operationsNav,
    improvementsNav,
    orgIssuesNav,
    teamMeetingsNav,
    cuttysarkNav,
    dbDictionaryNav,
    knowledgeWikiNav,
    sreManagementNav,
    ccUsageNav,
    ccCommandMatrixNav,
    mcpUsageNav,
  ];
  // allDefs は 'whole'（画面に紐づかない話）を使わないので実際には安全だが、NavDef.key の
  // 型は PilabArea 全体（'whole' を含む）なので、PageKey へのキャストで橋渡しする。
  const defs: NavDef[] = allDefs.filter((def) => canAccess(def.key as PageKey));

  // 空グループ（items.length === 0）を返すと共有 Sidebar が group.items[0].key を
  // 読みに行って TypeError になるため、ナビ項目が無い external 社員の場合は空配列を返す。
  if (defs.length === 0) {
    return [];
  }

  // どの def キーが defs に存在するかを高速に確認する Set。
  // 「子が含まれるが親が含まれない」場合の孤児ネスト判定に使う。
  const presentKeys = new Set(defs.map((def) => def.key));

  return [
    {
      items: defs.map((def) => {
        const parentKey = NESTED_CHILD_TO_PARENT.get(def.key);
        // 親が defs に居る場合のみ nested + parentKey を付与（孤児ネスト防止）。
        // parentKey を渡すと共有 Sidebar 側で親の折り畳み状態に連動する。
        const isNestedChild = parentKey !== undefined && presentKeys.has(parentKey);
        return {
          key: def.key,
          label: t(`nav.${def.key}`),
          path: PILAB_NAV_AREA_PATHS[def.key],
          icon: def.icon,
          nested: isNestedChild,
          // ダッシュボード / PI Div管理 / プロジェクト管理 / SRE管理は配下 subpage をまとめるフォルダ項目。
          // クリックしても遷移せず子の展開のみを行うよう folder フラグを付ける。
          ...(FOLDER_KEYS.has(def.key) ? { folder: true } : {}),
          ...(isNestedChild ? { parentKey } : {}),
        };
      }),
    },
  ];
}
